import { QrCode } from "lucide-react";
import { formatRoom, formatGrade } from "@/lib/classroom";
import { formatDateRange, formatMoney, formatNumber } from "@/lib/format";

/**
 * Web-friendly preview of the take-home sheet PDF.
 *
 * The real sheet is rendered server-side from app/views/take_home_sheets/_sheet.html.erb
 * and converted to PDF via Grover. That template is print-styled and self-
 * contained, which makes it awkward to embed inline in a React help
 * article. This component mirrors the same layout + section names so a
 * help-centre reader sees the same anatomy a parent sees on paper:
 *
 *   - Campaign + school name header
 *   - Context blurb ("what's a read-a-thon")
 *   - Student name + classroom + activation code + big QR
 *   - 3-step "what to expect" list
 *   - Optional rewards list
 *   - Footer with dates and goals
 *
 * If the real PDF template gains or loses a section, update this too.
 * Linked file: app/views/take_home_sheets/_sheet.html.erb
 *
 * Props:
 *   school     — { name }
 *   campaign   — { name, start_at, end_at, money_goal_cents, minute_goal, description }
 *   student    — { full_name, first_name, classroom_name, grade, claim_code }
 *   milestones — [{ kind, threshold_amount, prize }] (optional)
 */
export default function TakeHomeSheetPreview({ school, campaign, student, milestones = [] }) {
  const moneyMs = milestones.filter((m) => m.kind === "money");
  const minuteMs = milestones.filter((m) => m.kind === "minutes");

  return (
    <div className="rounded-lg border border-border bg-card p-6 md:p-8 max-w-md mx-auto space-y-5 text-foreground">
      {/* Header */}
      <header className="text-center border-b border-border pb-3">
        <h1 className="font-display text-base font-semibold tracking-tight">
          {campaign.name} · {school.name}
        </h1>
      </header>

      {/* Context */}
      <section className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          What's a read-a-thon?
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          A read-a-thon is a school fundraiser where students read — at school and at home —
          and family or friends pledge donations to cheer them on. <strong className="text-foreground">PagePledge</strong> is how
          {" "}{school.name} runs it online: you'll follow your child's reading and share their page with anyone who wants to support.
        </p>
        {campaign.description && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-3">
              A note from {school.name}
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">{campaign.description}</p>
          </>
        )}
      </section>

      {/* Student claim block */}
      <section className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 text-center space-y-3">
        <div>
          <h2 className="font-display text-xl font-semibold">{student.full_name}</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {formatRoom(student.classroom_name)}
            {student.grade ? ` · ${formatGrade(student.grade)}` : ""}
          </p>
        </div>

        <div className="grid grid-cols-[1fr_auto] gap-4 items-center text-left">
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Parents & family — start here
            </p>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Scan the QR code to activate {student.first_name}'s reading page — or use the code below at <strong className="text-foreground">pagepledge.com/activate</strong>.
            </p>
            <div className="font-mono text-base font-semibold text-primary tracking-wider">
              {student.claim_code}
            </div>
            <p className="text-[10px] text-muted-foreground italic">
              We'll email a secure link to your inbox — no password to remember.
            </p>
          </div>
          <div className="text-center">
            <div className="h-20 w-20 border-2 border-foreground/20 rounded-md flex items-center justify-center bg-card">
              <QrCode className="h-14 w-14 text-foreground/40" />
            </div>
            <p className="text-[9px] text-muted-foreground mt-1">Scan with your phone camera</p>
          </div>
        </div>
      </section>

      {/* Next steps */}
      <section className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          What to expect
        </div>
        <ol className="space-y-1.5 text-[11px]">
          <li className="flex gap-2">
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold shrink-0">1</span>
            <div><strong className="text-foreground">Activate {student.first_name}'s page.</strong> <span className="text-muted-foreground">Scan the QR or use the code above.</span></div>
          </li>
          <li className="flex gap-2">
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold shrink-0">2</span>
            <div><strong className="text-foreground">Log reading at home.</strong> <span className="text-muted-foreground">Bedtime stories and library books all count.</span></div>
          </li>
          <li className="flex gap-2">
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold shrink-0">3</span>
            <div><strong className="text-foreground">Share their page.</strong> <span className="text-muted-foreground">You'll get a unique link to send to family and friends.</span></div>
          </li>
        </ol>
      </section>

      {/* Rewards */}
      {milestones.length > 0 && (
        <section className="space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Rewards the school can earn together
          </div>
          <div className="grid grid-cols-2 gap-3 text-[11px]">
            {moneyMs.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground/80 mb-1">Fundraising</div>
                <ul className="space-y-1">
                  {moneyMs.map((m, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="text-primary font-tabular">{formatMoney(m.threshold_amount)}</span>
                      <span className="text-foreground/80 truncate">{m.prize}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {minuteMs.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground/80 mb-1">Reading minutes</div>
                <ul className="space-y-1">
                  {minuteMs.map((m, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="text-primary font-tabular">{formatNumber(m.threshold_amount)} min</span>
                      <span className="text-foreground/80 truncate">{m.prize}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-border pt-3 grid grid-cols-3 gap-2 text-center">
        <FooterCell label="Campaign dates" value={formatDateRange(campaign.start_at, campaign.end_at)} />
        {campaign.minute_goal && (
          <FooterCell label="Reading goal" value={`${formatNumber(campaign.minute_goal)} min`} />
        )}
        {campaign.money_goal_cents && (
          <FooterCell label="Fundraising goal" value={formatMoney(campaign.money_goal_cents)} />
        )}
      </footer>

      <div className="text-center text-[9px] uppercase tracking-wider text-muted-foreground/60">
        PagePledge · read-a-thons made simple
      </div>
    </div>
  );
}

function FooterCell({ label, value }) {
  return (
    <div>
      <div className="text-[8px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-[10px] font-semibold text-warning-foreground">{value}</div>
    </div>
  );
}

